import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Loader2, Pencil, Plus, Star } from 'lucide-react';
import { apiClient } from '../../../lib/api';
import type { Company } from './types';
import { labelOf } from './types';

const COMPANY_TYPES = [
  { value: 'ltd', label: 'חברה בע״מ' },
  { value: 'licensed_dealer', label: 'עוסק מורשה' },
  { value: 'exempt_dealer', label: 'עוסק פטור' },
  { value: 'partnership', label: 'שותפות' },
  { value: 'nonprofit', label: 'מלכ״ר' },
];

type FormState = {
  short_label: string;
  legal_name: string;
  company_id_number: string;
  company_type: string;
  industry: string;
  is_primary: boolean;
  founded_year: string;
  notes: string;
};

const empty: FormState = {
  short_label: '',
  legal_name: '',
  company_id_number: '',
  company_type: 'ltd',
  industry: '',
  is_primary: false,
  founded_year: '',
  notes: '',
};

export function CompaniesTab({ caseId }: { caseId: string }) {
  const qc = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<FormState>(empty);

  const companiesQuery = useQuery<Company[]>({
    queryKey: ['companies', caseId],
    queryFn: () => apiClient.get<Company[]>(`/companies?case_id=${caseId}`),
  });

  const create = useMutation<Company, Error, FormState>({
    mutationFn: (s) =>
      apiClient.post<Company>('/companies', {
        case_id: caseId,
        short_label: s.short_label,
        legal_name: s.legal_name || null,
        company_id_number: s.company_id_number || null,
        company_type: s.company_type,
        industry: s.industry || null,
        is_primary: s.is_primary,
        founded_year: s.founded_year ? Number(s.founded_year) : null,
        notes: s.notes || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['companies', caseId] });
      setForm(empty);
      setAdding(false);
    },
  });

  const update = useMutation<Company, Error, { id: string; state: FormState }>({
    mutationFn: ({ id, state }) =>
      apiClient.patch<Company>(`/companies/${id}`, {
        short_label: state.short_label,
        legal_name: state.legal_name || null,
        company_id_number: state.company_id_number || null,
        company_type: state.company_type,
        industry: state.industry || null,
        is_primary: state.is_primary,
        founded_year: state.founded_year ? Number(state.founded_year) : null,
        notes: state.notes || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['companies', caseId] });
      setForm(empty);
      setEditingId(null);
    },
  });

  const startEdit = (c: Company) => {
    setForm({
      short_label: c.short_label,
      legal_name: c.legal_name ?? '',
      company_id_number: c.company_id_number ?? '',
      company_type: c.company_type,
      industry: c.industry ?? '',
      is_primary: c.is_primary,
      founded_year: c.founded_year?.toString() ?? '',
      notes: c.notes ?? '',
    });
    setEditingId(c.id);
    setAdding(false);
  };

  const cancel = () => {
    setForm(empty);
    setEditingId(null);
    setAdding(false);
  };

  const submit = () => {
    if (editingId) update.mutate({ id: editingId, state: form });
    else create.mutate(form);
  };

  const isPending = create.isPending || update.isPending;
  const error = create.error || update.error;
  const showForm = adding || editingId !== null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-navy">חברות בקייס</h2>
        {!showForm && (
          <button
            type="button"
            onClick={() => {
              setForm(empty);
              setAdding(true);
            }}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            הוסף חברה
          </button>
        )}
      </div>

      {showForm && (
        <div className="card">
          <h3 className="font-semibold text-navy mb-4">
            {editingId ? 'עריכת חברה' : 'חברה חדשה'}
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="תווית קצרה (חובה)">
              <input
                type="text"
                value={form.short_label}
                onChange={(e) => setForm({ ...form, short_label: e.target.value })}
                placeholder='לדוגמה: "א"'
                className="input"
              />
            </Field>
            <Field label="שם משפטי מלא">
              <input
                type="text"
                value={form.legal_name}
                onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
                className="input"
              />
            </Field>
            <Field label="ח.פ. / ע.מ.">
              <input
                type="text"
                value={form.company_id_number}
                onChange={(e) =>
                  setForm({ ...form, company_id_number: e.target.value })
                }
                className="input"
              />
            </Field>
            <Field label="סוג ישות">
              <select
                value={form.company_type}
                onChange={(e) => setForm({ ...form, company_type: e.target.value })}
                className="input"
              >
                {COMPANY_TYPES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="ענף">
              <input
                type="text"
                value={form.industry}
                onChange={(e) => setForm({ ...form, industry: e.target.value })}
                className="input"
              />
            </Field>
            <Field label="שנת ייסוד">
              <input
                type="number"
                value={form.founded_year}
                onChange={(e) => setForm({ ...form, founded_year: e.target.value })}
                className="input"
              />
            </Field>
            <Field label="הערות" className="md:col-span-3">
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                className="input"
              />
            </Field>
            <Field label="" className="md:col-span-3">
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={form.is_primary}
                  onChange={(e) =>
                    setForm({ ...form, is_primary: e.target.checked })
                  }
                  className="rounded"
                />
                חברה ראשית בקבוצה
              </label>
            </Field>
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 mt-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>שגיאה: {error.message}</span>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={cancel}
              disabled={isPending}
              className="btn-secondary disabled:opacity-50"
            >
              ביטול
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!form.short_label || isPending}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            >
              {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              שמור
            </button>
          </div>
        </div>
      )}

      <div className="card !p-0 overflow-hidden">
        {companiesQuery.isLoading ? (
          <div className="p-8 text-center text-slate-500">טוען…</div>
        ) : companiesQuery.isError ? (
          <div className="p-8 text-center text-red-600 text-sm">
            שגיאה בטעינה: {(companiesQuery.error as Error).message}
          </div>
        ) : (companiesQuery.data?.length ?? 0) === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">
            עוד לא הוזנו חברות. לחץ "הוסף חברה" כדי להתחיל.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-4 py-3 text-right font-medium">תווית</th>
                <th className="px-4 py-3 text-right font-medium">שם משפטי</th>
                <th className="px-4 py-3 text-right font-medium">ח.פ.</th>
                <th className="px-4 py-3 text-right font-medium">סוג</th>
                <th className="px-4 py-3 text-right font-medium">ענף</th>
                <th className="px-4 py-3 text-right font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(companiesQuery.data ?? []).map((c) => (
                <tr key={c.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="inline-flex items-center gap-1 font-medium">
                      {c.short_label}
                      {c.is_primary && (
                        <Star
                          className="w-3.5 h-3.5 text-amber-500 fill-amber-500"
                          aria-label="ראשית"
                        />
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">{c.legal_name ?? '—'}</td>
                  <td className="px-4 py-3 num">{c.company_id_number ?? '—'}</td>
                  <td className="px-4 py-3">{labelOf(COMPANY_TYPES, c.company_type)}</td>
                  <td className="px-4 py-3">{c.industry ?? '—'}</td>
                  <td className="px-4 py-3 text-left">
                    <button
                      type="button"
                      onClick={() => startEdit(c)}
                      className="text-slate-400 hover:text-business-primary transition"
                      aria-label="ערוך"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-slate-700 mb-1">
          {label}
        </label>
      )}
      {children}
    </div>
  );
}
